// src/components/enrollments/EnrollmentsProvider.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import enrollmentsService from '../../services/enrollmentsService';
import { useAuth } from '../auth/AuthContext';

const EnrollmentsContext = createContext();

export const useEnrollments = () => {
  const context = useContext(EnrollmentsContext);
  if (!context) {
    throw new Error('useEnrollments must be used within an EnrollmentsProvider');
  }
  return context;
};

export const EnrollmentsProvider = ({ children }) => {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Load enrollments from localStorage as fallback
  const loadEnrollmentsFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('classtraks_enrollments');
      if (stored) {
        const parsedEnrollments = JSON.parse(stored);
        if (Array.isArray(parsedEnrollments) && parsedEnrollments.length > 0) {
          console.log('EnrollmentsProvider - Loaded enrollments from localStorage:', parsedEnrollments);
          return parsedEnrollments;
        }
      }
    } catch (error) {
      console.error('Error loading enrollments from localStorage:', error);
    }
    return [];
  };

  // Default sample data for development (if no backend data available)
  const getDefaultEnrollments = () => [];

  // Fetch enrollments from database with localStorage fallback
  const fetchEnrollments = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('EnrollmentsProvider - Fetching enrollments from database...');
      const response = await enrollmentsService.getEnrollments(params);
      console.log('EnrollmentsProvider - Database response:', response);
      
      // If we got enrollments from database, use them
      if (response.enrollments && response.enrollments.length >= 0) {
        console.log('EnrollmentsProvider - Using database enrollments');
        const transformedEnrollments = enrollmentsService.transformEnrollmentsFromBackend(response.enrollments);
        setEnrollments(transformedEnrollments);
        
        // Sync to localStorage for future fallback
        localStorage.setItem('classtraks_enrollments', JSON.stringify(transformedEnrollments));
        
        if (response.pagination) {
          setPagination(response.pagination);
        }
        
        return transformedEnrollments;
      }
      
      // If no database enrollments, check localStorage as fallback
      console.log('EnrollmentsProvider - No database enrollments, checking localStorage...');
      const localEnrollments = loadEnrollmentsFromLocalStorage();
      if (localEnrollments.length > 0) {
        console.log('EnrollmentsProvider - Using localStorage enrollments as fallback');
        setEnrollments(localEnrollments);
        return localEnrollments;
      }
      
      // If no localStorage data, use empty array (no sample data for enrollments)
      console.log('EnrollmentsProvider - No enrollments found, starting with empty array');
      const defaultEnrollments = getDefaultEnrollments();
      setEnrollments(defaultEnrollments);
      return defaultEnrollments;
      
    } catch (error) {
      console.error('EnrollmentsProvider - Error fetching from database:', error);
      setError(error.message);
      
      // Fallback to localStorage on database error
      console.log('EnrollmentsProvider - Database error, falling back to localStorage...');
      const localEnrollments = loadEnrollmentsFromLocalStorage();
      if (localEnrollments.length > 0) {
        setEnrollments(localEnrollments);
        return localEnrollments;
      }
      
      // Final fallback to empty array
      console.log('EnrollmentsProvider - Using empty array as final fallback');
      const defaultEnrollments = getDefaultEnrollments();
      setEnrollments(defaultEnrollments);
      return defaultEnrollments;
    } finally {
      setLoading(false);
    }
  };

  // Get single enrollment with localStorage fallback
  const getEnrollment = async (id) => {
    try {
      console.log('EnrollmentsProvider - Getting enrollment with ID:', id);
      
      // Try database first
      try {
        const response = await enrollmentsService.getEnrollment(id);
        console.log('EnrollmentsProvider - Found enrollment in database:', response);
        return enrollmentsService.transformEnrollmentFromBackend(response);
      } catch (dbError) {
        console.error('EnrollmentsProvider - Database fetch failed, checking local enrollments:', dbError);
        
        // Fallback to local enrollments
        const localEnrollment = enrollments.find(enrollment => 
          enrollment.id === id || enrollment.id === parseInt(id) || String(enrollment.id) === String(id)
        );
        
        if (localEnrollment) {
          console.log('EnrollmentsProvider - Found enrollment in local state:', localEnrollment);
          return localEnrollment;
        }
        
        throw new Error('Enrollment not found');
      }
    } catch (error) {
      console.error('EnrollmentsProvider - Error fetching enrollment:', error);
      setError(error.message);
      throw error;
    }
  };

  // Create new enrollment
  const createEnrollment = async (enrollmentData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('EnrollmentsProvider - Creating enrollment:', enrollmentData);
      
      // Try to save to database
      try {
        const newEnrollment = await enrollmentsService.createEnrollment(enrollmentData);
        console.log('EnrollmentsProvider - Enrollment created in database:', newEnrollment);
        
        const transformedEnrollment = enrollmentsService.transformEnrollmentFromBackend(newEnrollment);
        
        // Add to local state
        setEnrollments(prev => {
          const updatedEnrollments = [transformedEnrollment, ...prev];
          // Update localStorage with the new state
          localStorage.setItem('classtraks_enrollments', JSON.stringify(updatedEnrollments));
          return updatedEnrollments;
        });
        
        // Update pagination
        setPagination(prev => ({
          ...prev,
          totalItems: prev.totalItems + 1
        }));
        
        return transformedEnrollment;
      } catch (dbError) {
        console.error('EnrollmentsProvider - Database save failed:', dbError);
        throw dbError; // Don't create local fallback for enrollments
      }
    } catch (error) {
      console.error('EnrollmentsProvider - Error creating enrollment:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update existing enrollment
  const updateEnrollment = async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('EnrollmentsProvider - Updating enrollment:', id, updates);
      
      // Try database first
      try {
        const updatedEnrollment = await enrollmentsService.updateEnrollment(id, updates);
        console.log('EnrollmentsProvider - Enrollment updated in database:', updatedEnrollment);
        
        const transformedEnrollment = enrollmentsService.transformEnrollmentFromBackend(updatedEnrollment);
        
        // Update local state
        setEnrollments(prev => {
          const updatedEnrollments = prev.map(enrollment => 
            enrollment.id === id ? transformedEnrollment : enrollment
          );
          // Update localStorage with the new state
          localStorage.setItem('classtraks_enrollments', JSON.stringify(updatedEnrollments));
          return updatedEnrollments;
        });
        
        return transformedEnrollment;
      } catch (dbError) {
        console.error('EnrollmentsProvider - Database update failed:', dbError);
        throw dbError;
      }
    } catch (error) {
      console.error('EnrollmentsProvider - Error updating enrollment:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Cancel single enrollment
  const cancelEnrollment = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('EnrollmentsProvider - Cancelling enrollment:', id);
      
      // Try database first
      try {
        await enrollmentsService.cancelEnrollment(id);
        console.log('EnrollmentsProvider - Enrollment cancelled in database');
        
        // Update local state (set status to cancelled)
        setEnrollments(prev => {
          const updatedEnrollments = prev.map(enrollment => 
            enrollment.id === id ? { ...enrollment, enrollmentStatus: 'cancelled' } : enrollment
          );
          // Update localStorage with the new state
          localStorage.setItem('classtraks_enrollments', JSON.stringify(updatedEnrollments));
          return updatedEnrollments;
        });
        
        return true;
      } catch (dbError) {
        console.error('EnrollmentsProvider - Database cancel failed:', dbError);
        throw dbError;
      }
    } catch (error) {
      console.error('EnrollmentsProvider - Error cancelling enrollment:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Cancel multiple enrollments
  const cancelEnrollments = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('EnrollmentsProvider - Cancelling multiple enrollments:', ids);
      
      // Try database cancellations
      try {
        await enrollmentsService.cancelEnrollments(ids);
        console.log('EnrollmentsProvider - Enrollments cancelled in database');
        
        // Update local state
        setEnrollments(prev => {
          const updatedEnrollments = prev.map(enrollment => 
            ids.includes(enrollment.id) ? { ...enrollment, enrollmentStatus: 'cancelled' } : enrollment
          );
          // Update localStorage with the new state
          localStorage.setItem('classtraks_enrollments', JSON.stringify(updatedEnrollments));
          return updatedEnrollments;
        });
        
        return true;
      } catch (dbError) {
        console.error('EnrollmentsProvider - Database bulk cancel failed:', dbError);
        throw dbError;
      }
    } catch (error) {
      console.error('EnrollmentsProvider - Error cancelling enrollments:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get enrollment statistics
  const getEnrollmentStats = async () => {
    try {
      const stats = await enrollmentsService.getEnrollmentStats();
      return stats;
    } catch (error) {
      console.error('Error fetching enrollment stats:', error);
      setError(error.message);
      throw error;
    }
  };

  // Get available classes for a student
  const getAvailableClasses = async (studentId) => {
    try {
      const response = await enrollmentsService.getAvailableClasses(studentId);
      return response;
    } catch (error) {
      console.error('Error fetching available classes:', error);
      setError(error.message);
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Bulk update enrollments
  const bulkUpdateEnrollments = async (enrollmentIds, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('EnrollmentsProvider - Bulk updating enrollments:', enrollmentIds, updates);
      
      const result = await enrollmentsService.bulkUpdateEnrollments(enrollmentIds, updates);
      console.log('EnrollmentsProvider - Bulk update result:', result);
      
      // Refresh enrollments to get updated data
      await fetchEnrollments();
      
      return result;
    } catch (error) {
      console.error('EnrollmentsProvider - Error bulk updating enrollments:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh enrollments from database
  const refreshEnrollments = () => {
    return fetchEnrollments();
  };

  // Load initial data and refetch when user changes
  useEffect(() => {
    if (user) {
      // Clear existing enrollments and refetch for new user
      setEnrollments([]);
      fetchEnrollments();
    }
  }, [user?.id]); // Refetch when user ID changes

  const value = {
    enrollments,
    loading,
    error,
    pagination,
    fetchEnrollments,
    getEnrollment,
    createEnrollment,
    updateEnrollment,
    bulkUpdateEnrollments,
    cancelEnrollment,
    cancelEnrollments,
    getEnrollmentStats,
    getAvailableClasses,
    clearError,
    refreshEnrollments
  };

  return (
    <EnrollmentsContext.Provider value={value}>
      {children}
    </EnrollmentsContext.Provider>
  );
};